import { useAnonRestApi, useAuthRestApi } from '../common';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { DataTransformationService } from '@vivli/shared/infrastructure/service';
import { ICodeItem, IReportItem, IStatusUpdate } from '@vivli/shared/infrastructure/interface';
import {
    IDataProvider,
    IDataRequest,
    IDataRequestsApi,
    IDataRequestSummary,
    IDataRequestSummaryPage,
    IDataRequestSummaryPageRequest,
    IInProcessDataRequest,
    IProvisionOptions,
    IRequestedStudyModification,
    IResearchTeamMemberDetail,
    ISafetyConcern,
    ISafetyConcernSubmission,
    ITeamMemberRequestResult
} from '@vivli/features/data-requests/infrastructure/interface';
import { ApprovalStatusEnum, DataRequestStatusEnum, StudyRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { IResultRequest } from '@vivli/features/results-request/infrastructure/interface';
import { IDataPackage } from '@vivli/shared/features/data-package/infrastructure/interface';
import { IVirtualMachine } from '@vivli/features/virtual-machine/infrastructure/interface';
import { ReportDataTypeEnum } from '@vivli/shared/infrastructure/enum';
import { useDataRequestsByAppType } from '@vivli/features/data-requests/infrastructure/hook';
import { useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';

export const useDataRequestsApi = (): IDataRequestsApi => {
    const { handleGet, handlePost, handlePut, handleDelete } = useAuthRestApi();
    const { isAmr } = useAssignedAppTypeContext();
    const dataRequestsByAppTypeFilter = useDataRequestsByAppType(isAmr);

    const anonRestApi = useAnonRestApi();

    const getDataRequests = (): Observable<IDataRequest[]> => {
        return handleGet<IDataRequest[]>('/dataRequests').pipe(map(dataRequestsByAppTypeFilter));
    };

    const getDataRequestSummaries = (assignedAppType: string): Observable<IDataRequestSummary[]> => {
        return handleGet<IDataRequestSummary[]>(`/dataRequests/assignedAppType/${assignedAppType}/statuses`);
    };

    // DEV-1003: one page of one tab of the Data Requests list, filtered, sorted and paged on the server.
    const getDataRequestSummaryPage = (
        assignedAppType: string,
        request: IDataRequestSummaryPageRequest
    ): Observable<IDataRequestSummaryPage> => {
        const query = new URLSearchParams();
        Object.entries(request).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return;
            }

            // DEV-958: a list binds on the server from one repeated key per item (statuses=Draft&statuses=Approved)
            if (Array.isArray(value)) {
                value.forEach((item) => query.append(key, String(item)));
                return;
            }

            query.set(key, String(value));
        });

        return handleGet<IDataRequestSummaryPage>(`/dataRequests/assignedAppType/${assignedAppType}/summaries/page?${query.toString()}`);
    };

    const getMyDataRequests = (): Observable<IDataRequest[]> => {
        return handleGet<IDataRequest[]>('/dataRequests/myDataRequests').pipe(map(dataRequestsByAppTypeFilter));
    };

    const getDataRequest = (dataRequestId: string): Observable<IDataRequest> => {
        return handleGet<IDataRequest>(`/dataRequests/${dataRequestId}`);
    };

    const getDataRequestAnonymouslyByDoi = (dataRequestDoi: string): Observable<IDataRequest> => {
        return anonRestApi.handleGet<IDataRequest>(`/dataRequests/${dataRequestDoi}/metadata`);
    };

    const getDataRequestByVivliId = (dataRequestDoi: string): Observable<IDataRequest> => {
        return handleGet<IDataRequest>(`/dataRequests/vivliId/${encodeURIComponent(dataRequestDoi)}`);
    };

    const getReport = (
        dataType: ReportDataTypeEnum,
        dateString: string,
        code: string,
        contributor?: string,
        queryString?: string
    ): Observable<IReportItem[]> => {
        return handleGet<IReportItem[]>(`/reports/${dataType}/${dateString}/${code}/${contributor}?${queryString}`).pipe(
            map(DataTransformationService.formatReportItems)
        );
    };

    const updateDataRequestStatus = (dataRequestId: string, statusUpdate: IStatusUpdate): Observable<IDataRequest> => {
        return handlePost<IDataRequest>(`/DataRequests/${dataRequestId}/statusupdate`, statusUpdate);
    };

    const createDataPackageForStudy = (dataRequestId: string, studyId: string): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/dataRequests/${dataRequestId}/studies/${studyId}/createDataPackage`);
    };

    const setDataRequestStudyStatus = (
        dataRequestId: string,
        studyId: string,
        requestedStudyStatus: StudyRequestStatusEnum,
        comment: string
    ): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/dataRequests/${dataRequestId}/studies/${studyId}/status`, {
            requestedStudyStatus,
            comment
        });
    };

    const removeDataRequestStudy = (dataRequestId: string, studyId: string): Observable<IDataRequest> => {
        return handleDelete<IDataRequest>(`/dataRequests/${dataRequestId}/studies/${studyId}`);
    };

    const deleteDataRequest = (dataRequestId: string): Observable<void> => {
        return handleDelete<void>(`/dataRequests/${dataRequestId}`);
    };

    const setTeamMemberAdminApproval = (
        dataRequestId: string,
        teamMember: IResearchTeamMemberDetail,
        isApprove: string
    ): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(
            `/DataRequests/${dataRequestId}/researchteam/adminapproval?memberId=${teamMember.teamMemberId}&isApprove=${isApprove}`,
            teamMember
        );
    };

    const changeDataRequestOwner = (dataRequestId: string, teamMember: IResearchTeamMemberDetail): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/DataRequests/${dataRequestId}/owner`, teamMember);
    };

    const revokeVmAccess = (dataRequestId: string, memberId: string): Observable<IDataRequest> => {
        return handleDelete<IDataRequest>(`/DataRequests/${dataRequestId}/activations/revoke?memberId=${memberId}`);
    };

    // DEV-510: direct grant of access to the data request (replaces the invite-based grant path).
    const grantAccessToDataRequest = (dataRequestId: string, teamMemberId: string): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/DataRequests/${dataRequestId}/researchteam/grantaccess?memberId=${teamMemberId}`, {});
    };

    const removeResearchTeamMember = (dataRequestId: string, teamMemberId: string): Observable<IDataRequest> => {
        return handleDelete<IDataRequest>(`/DataRequests/${dataRequestId}/researchteam?memberId=${teamMemberId}`);
    };

    const editResearchTeamMember = (dataRequestId: string, teamMember: IResearchTeamMemberDetail): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/DataRequests/${dataRequestId}/researchteam?memberId=${teamMember.teamMemberId}`, teamMember);
    };

    const addResearchTeamMember = (dataRequestId: string, teamMember: IResearchTeamMemberDetail): Observable<ITeamMemberRequestResult> => {
        return handlePost<ITeamMemberRequestResult>(`/DataRequests/${dataRequestId}/researchteam`, teamMember);
    };

    const startHeuristics = (dataRequestId: string, resultRequestId: string, dataPackage: IDataPackage): Observable<IResultRequest> => {
        return handlePost<IResultRequest>(
            `/dataRequests/${dataRequestId}/researchEnvironment/${resultRequestId}/StartHeuristics/true`,
            dataPackage
        );
    };

    const notifyOfDuaUpload = (dataRequestId: string): Observable<void> => {
        return handleGet(`/dataRequests/${dataRequestId}/notify/vivliAdmin/duaUpload`);
    };

    const updateDataRequest = (dataRequest: IDataRequest): Observable<IDataRequest> => {
        return handlePut<IDataRequest>('/dataRequests', dataRequest);
    };

    const submitDataRequest = (dataRequest: IDataRequest): Observable<IDataRequest> => {
        return handlePut<IDataRequest>('/dataRequests/submit', dataRequest);
    };

    const createDataPackageForRequest = (dataRequestId: string): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/dataRequests/${dataRequestId}/createDataPackage`, {});
    };

    const getDataProviders = (dataRequestId: string, isInProcess: boolean): Observable<IDataProvider[]> => {
        return handleGet<IDataProvider[]>(`/dataRequests/${dataRequestId}/unlistedstudies/dataproviders/${isInProcess}`);
    };

    const checkForListedStudy = (
        studyExternalId: string
    ): Observable<{ found: boolean; studyTitle?: string; nctId?: string; sponsorProtocolId?: string }> => {
        return handleGet(`/dataRequests/unlistedstudies/checkForListedStudy?studyExternalId=${encodeURIComponent(studyExternalId)}`);
    };

    const addUnlistedStudy = (
        dataRequestId: string,
        unlistedStudy: { dataProviderOrgId: string; studyTitle: string; nctId: string; sponsorProtocolId: string }
    ): Observable<IDataRequest> => {
        return handlePost<IDataRequest>(`/dataRequests/${dataRequestId}/unlistedstudies/addToDataRequest`, unlistedStudy);
    };

    const addUnlistedStudyToInProcessRequest = (
        dataRequestId: string,
        unlistedStudy: { dataProviderOrgId: string; studyTitle: string; nctId: string; sponsorProtocolId: string }
    ): Observable<IDataRequest> => {
        return handlePost<IDataRequest>(`/dataRequests/${dataRequestId}/studies/addUnlistedInProcess`, unlistedStudy);
    };

    const postStatusUpdate = (dataRequestId: string, statusUpdate: IStatusUpdate): Observable<IDataRequest> => {
        return handlePost<IDataRequest>(`/DataRequests/${dataRequestId}/statusupdate`, statusUpdate);
    };

    const setTracking = (dataRequestId: string, tracking: boolean): Observable<IDataRequest> => {
        return handlePost<IDataRequest>(`/DataRequests/${dataRequestId}/tracking/${tracking}`);
    };

    const setDataRequestStatus = (dataRequestId: string, status: DataRequestStatusEnum, comment: string): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/dataRequests/${dataRequestId}/status`, { status, comment });
    };

    const getSafetyConcerns = (dataRequestId: string): Observable<ISafetyConcern[]> => {
        return handleGet<ISafetyConcern[]>(`/DataRequests/${dataRequestId}/safetyconcerns`);
    };

    const postSafetyConcern = (dataRequestId: string, safetyConcernSubmission: ISafetyConcernSubmission): Observable<void> => {
        return handlePost<void>(`/DataRequests/${dataRequestId}/safetyconcerns`, safetyConcernSubmission);
    };

    const createOrUpdateDoi = (dataRequest: IDataRequest): Observable<IDataRequest> => {
        return handlePut<IDataRequest>('/dataRequests/secondaryanalysisdoi', dataRequest);
    };

    const getApprovalOrgs = (dataRequestId: string): Observable<string[]> => {
        return handleGet<string[]>(`/datarequests/${dataRequestId}/approve/approvableOrgs`);
    };

    const approveRequest = (
        dataRequestId: string,
        comment: string,
        orgId: string,
        rejectionReasonItems: ICodeItem[],
        status: ApprovalStatusEnum
    ): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/dataRequests/${dataRequestId}/approve/${status}`, {
            comment,
            orgId,
            rejectionReasonItems
        });
    };

    const addStudyToDataRequest = (options: { studyId: string; targetDataRequestTitle: string }): Observable<IDataRequest> => {
        return handlePost<IDataRequest>('/dataRequests/studies/addToDataRequest', options);
    };

    const addStudyToDataRequestInProcess = (options: {
        studySponsorIdOrNctId: string;
        dataRequestId: string;
    }): Observable<IInProcessDataRequest> => {
        return handlePost<IInProcessDataRequest>('/dataRequests/studies/addToDataRequest/inprocesslisted', options);
    };

    const addListedStudyByIdToDataRequest = (options: {
        studySponsorIdOrNctId: string;
        dataRequestId: string;
    }): Observable<IInProcessDataRequest> => {
        return handlePost<IInProcessDataRequest>('/dataRequests/studies/addToDataRequest/byid', options);
    };

    const updateStudyDownloadable = (dataRequestId: string, studyId: string, turnDownloadableOn: boolean): Observable<IDataRequest> => {
        return handlePost<IDataRequest>(`/DataRequests/${dataRequestId}/${studyId}/downloadableSetting/${turnDownloadableOn}`);
    };

    const attachSignatureToRequest = (requestId: string, signatureId: string): Observable<IDataRequest> => {
        return handlePost<IDataRequest>(`/dataRequests/${requestId}/signature`, signatureId);
    };

    const getVirtualMachine = (dataRequestId: string): Observable<IVirtualMachine> => {
        return handleGet(`/dataRequests/${dataRequestId}/researchEnvironment`);
    };

    const provisionVirtualMachine = (dataRequestId: string, options: IProvisionOptions): Observable<void> => {
        return handlePost(`/dataRequests/${dataRequestId}/researchEnvironment/requestToProvision`, options);
    };

    const retryProvisionVirtualMachine = (dataRequestId: string, options: IProvisionOptions): Observable<void> => {
        return handlePost(`/dataRequests/${dataRequestId}/researchEnvironment/retryRequestToProvision`, options);
    };

    const deProvisionVirtualMachine = (dataRequestId: string, options: IProvisionOptions): Observable<void> => {
        return handlePost(`/dataRequests/${dataRequestId}/researchEnvironment/requestToDeprovision`, options);
    };

    const startVirtualMachine = (dataRequestId: string, options: IProvisionOptions): Observable<void> => {
        return handlePost(`/dataRequests/${dataRequestId}/researchEnvironment/requestToStart`, options);
    };

    const stopVirtualMachine = (dataRequestId: string, options: IProvisionOptions): Observable<void> => {
        return handlePost(`/dataRequests/${dataRequestId}/researchEnvironment/requestToStop`, options);
    };

    const addDataPackagesToVirtualMachine = (dataRequestId: string, options: IProvisionOptions): Observable<void> => {
        return handlePost(`/dataRequests/${dataRequestId}/researchEnvironment/requestToAddDataPackages`, options);
    };

    const getVmPasswordForActiveUser = (dataRequestId: string): Observable<IVirtualMachine> => {
        return handleGet(`/dataRequests/${dataRequestId}/researchEnvironment/authorizedUserPassword`);
    };

    const getNumberOfStudiesProvided = (dataRequestId: string): Observable<number> => {
        return handleGet(`/dataRequests/${dataRequestId}/providedStudies`);
    };

    const modifyRequestedStudySettings = (params: IRequestedStudyModification): Observable<IDataRequest> => {
        return handlePut<IDataRequest>(`/dataRequests/requestedStudyModification`, params);
    };

    const downloadDataRequestPdf = (dataRequestId: string, showRiskScore: boolean): Observable<string> => {
        return handleGet<string>(`/dataRequests/downloadPdf/${dataRequestId}/${showRiskScore}`);
    };

    const saveDataRequestPdf = (dataRequestId: string): Observable<IDataRequest> => {
        return handlePost<IDataRequest>(`/dataRequests/${dataRequestId}/pdfVersion`);
    };

    const downloadPdfVersion = (dataRequestId: string, fileName: string): Observable<string> => {
        return handleGet<string>(`/dataRequests/${dataRequestId}/downloadPdfVersion/${fileName}`);
    };

    const downloadPdfComparison = (dataRequestId: string, fileNameFrom: string, fileNameTo: string): Observable<string> => {
        return handleGet<string>(`/dataRequests/pdfcomparison/${dataRequestId}/${fileNameFrom}/${fileNameTo}`);
    };

    return {
        getDataRequests,
        getDataRequestSummaries,
        getDataRequestSummaryPage,
        getMyDataRequests,
        getDataRequest,
        getDataRequestAnonymouslyByDoi,
        getDataRequestByVivliId,
        getReport,
        updateDataRequestStatus,
        createDataPackageForStudy,
        setDataRequestStudyStatus,
        removeDataRequestStudy,
        deleteDataRequest,
        setTeamMemberAdminApproval,
        changeDataRequestOwner,
        revokeVmAccess,
        grantAccessToDataRequest,
        removeResearchTeamMember,
        editResearchTeamMember,
        addResearchTeamMember,
        startHeuristics,
        notifyOfDuaUpload,
        updateDataRequest,
        submitDataRequest,
        createDataPackageForRequest,
        getDataProviders,
        checkForListedStudy,
        addUnlistedStudy,
        addUnlistedStudyToInProcessRequest,
        postStatusUpdate,
        setTracking,
        setDataRequestStatus,
        getSafetyConcerns,
        postSafetyConcern,
        createOrUpdateDoi,
        getApprovalOrgs,
        approveRequest,
        addStudyToDataRequest,
        addStudyToDataRequestInProcess,
        addListedStudyByIdToDataRequest,
        updateStudyDownloadable,
        attachSignatureToRequest,
        getVirtualMachine,
        provisionVirtualMachine,
        retryProvisionVirtualMachine,
        deProvisionVirtualMachine,
        startVirtualMachine,
        stopVirtualMachine,
        addDataPackagesToVirtualMachine,
        getVmPasswordForActiveUser,
        getNumberOfStudiesProvided,
        modifyRequestedStudySettings,
        downloadDataRequestPdf,
        saveDataRequestPdf,
        downloadPdfVersion,
        downloadPdfComparison
    };
};
